import React from 'react';
import { 
  HeartIcon, 
  ShieldCheckIcon, 
  SparklesIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  BuildingStorefrontIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import aboutImage from '../../assets/image1.jpeg';

const About = () => {
  return (
    <section className="relative w-full py-16 sm:py-24 bg-gradient-to-b from-slate-50 via-white to-emerald-50/30 overflow-hidden">
      {/* Decorative background ambient glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-200/25 rounded-full blur-3xl -translate-y-1/2 pointer-events-none"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-200/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute top-1/2 left-0 w-80 h-80 bg-blue-100/30 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
            About Us
          </h2>
        </div>
        
        {/* Main Grid: Visual Showcase (Left 5 Cols) & Single Clean Paragraph (Right 7 Cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Left Side: Professional Visual Showcase (5 Cols) */}
          <div className="lg:col-span-5 relative order-2 lg:order-1">
            <div className="relative group mx-auto max-w-md lg:max-w-none">
              
              {/* Back ambient glow */}
              <div className="absolute -inset-3 bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-cyan-500/20 rounded-[2.5rem] blur-xl opacity-75 group-hover:opacity-100 transition duration-500"></div>
              
              {/* Image Frame */}
              <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white bg-slate-100 h-[380px] sm:h-[460px] lg:h-[480px]">
                <img 
                  src={aboutImage} 
                  alt="About Sai Pharmacy" 
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
                />
                
                {/* Gradient vignette */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>

                {/* Floating Badge 1: Top Floating */}
                <div className="absolute top-4 left-4 sm:top-5 sm:left-5 bg-white/95 backdrop-blur-md rounded-2xl px-4 py-2.5 shadow-xl border border-white/80 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                    <BuildingStorefrontIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900">Sai Pharmacy</div>
                    <div className="text-[10px] font-medium text-emerald-600">Licensed Healthcare Provider</div>
                  </div>
                </div>



              </div>
            </div>
          </div>

          {/* Right Side: Enhanced Medical Card & Feature Grid (7 Cols) */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="relative bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/20 backdrop-blur-2xl rounded-3xl p-6 sm:p-10 border border-emerald-100/90 shadow-[0_20px_50px_rgba(16,185,129,0.08)] hover:shadow-[0_25px_60px_rgba(16,185,129,0.12)] transition-all duration-300 overflow-hidden">
              
              {/* Subtle Ambient Background Watermark Icon */}
              <div className="absolute -bottom-10 -right-10 text-emerald-100/50 pointer-events-none">
                <HeartIcon className="w-64 h-64 stroke-[1]" />
              </div>

              {/* Eyebrow Tag */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 text-emerald-800 border border-emerald-200/80 text-xs font-extrabold uppercase tracking-wider mb-4 shadow-2xs">
                <SparklesIcon className="w-4 h-4 text-emerald-600 animate-pulse" />
                <span>Patient-First Healthcare</span>
              </div>

              {/* Main Heading */}
              <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight mb-3">
                Family <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent">Health & Care</span>
              </h3>

              {/* Main Paragraph */}
              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed font-normal mb-6">
                Sai Pharmacy is committed to delivering 100% authentic medications, compassionate customer service, and expert pharmacist consultations. We bridge traditional medical care with modern inventory management to provide prompt, dependable, and affordable healthcare solutions for every patient, 365 days a year.
              </p>

              {/* 4 Feature Card Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-6 border-t border-emerald-100/80">
                
                {/* Feature 1 */}
                <div className="group flex items-start gap-3 p-3.5 rounded-2xl bg-white/90 border border-emerald-100 shadow-xs hover:border-emerald-300 hover:shadow-md hover:bg-emerald-50/40 transition-all duration-200">
                  <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                    <ShieldCheckIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900 leading-snug">100% Certified Genuine Medicines</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5 font-medium">Direct from licensed manufacturers</p>
                  </div>
                </div>

                {/* Feature 2 */}
                <div className="group flex items-start gap-3 p-3.5 rounded-2xl bg-white/90 border border-emerald-100 shadow-xs hover:border-emerald-300 hover:shadow-md hover:bg-emerald-50/40 transition-all duration-200">
                  <div className="w-9 h-9 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center shrink-0 group-hover:bg-teal-600 group-hover:text-white transition-colors">
                    <UserGroupIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900 leading-snug">Expert Pharmacist Consultations</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5 font-medium">Professional dosage & safety advice</p>
                  </div>
                </div>

                {/* Feature 3 */}
                <div className="group flex items-start gap-3 p-3.5 rounded-2xl bg-white/90 border border-emerald-100 shadow-xs hover:border-emerald-300 hover:shadow-md hover:bg-emerald-50/40 transition-all duration-200">
                  <div className="w-9 h-9 rounded-xl bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 group-hover:bg-cyan-600 group-hover:text-white transition-colors">
                    <CheckCircleIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900 leading-snug">Strict Cold Chain Storage</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5 font-medium">Temperature preserved vaccines & insulin</p>
                  </div>
                </div>

                {/* Feature 4 */}
                <div className="group flex items-start gap-3 p-3.5 rounded-2xl bg-white/90 border border-emerald-100 shadow-xs hover:border-emerald-300 hover:shadow-md hover:bg-emerald-50/40 transition-all duration-200">
                  <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                    <SparklesIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900 leading-snug">Fair & Transparent Pricing</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5 font-medium">Honest rates with GST invoice</p>
                  </div>
                </div>

              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default About;

